import { Link } from "@tanstack/react-router";
import { Play, Github, Twitter, Facebook, Instagram, Send, MessageCircle, Heart, Mail, Check } from "lucide-react";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { useTheme, type LanguageCode } from "@/lib/theme-context";
import { useSocialLinks } from "@/lib/social-links";

const NEWSLETTER_KEY = "rawan-newsletter-v1";

/** Tri-lingual helper: returns the right string for ar / en / fr. */
const tt = (lang: LanguageCode, ar: string, en: string, fr: string) =>
  lang === "ar" ? ar : lang === "fr" ? fr : en;

/** Zod schema — validated before we ever touch Firestore. */
const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(5)
  .max(254)
  .email();

/** Firestore-safe doc id derived from the email address. */
const docIdFor = (email: string) => email.replace(/[^a-z0-9._-]+/g, "_").slice(0, 200);

export function Footer() {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr: string) => tt(language, ar, en, fr);

  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [alreadyIn, setAlreadyIn] = useState(false);

  const [social] = useSocialLinks();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function subscribe(e: FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSubscribed(false);
    setAlreadyIn(false);

    // 1) Client-side validation with zod.
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setErrorMsg(t("بريد غير صالح", "Invalid email address", "Adresse e-mail invalide"));
      return;
    }
    const value = parsed.data;

    setBusy(true);
    try {
      const [{ collection, doc, getDoc, setDoc, serverTimestamp }, { getDb }] = await Promise.all([
        import("firebase/firestore"),
        import("@/lib/firebase"),
      ]);
      const db = getDb();
      if (!db) {
        setErrorMsg(t(
          "الخدمة غير متاحة حالياً.",
          "Service unavailable right now.",
          "Service indisponible pour le moment.",
        ));
        return;
      }

      const ref = doc(collection(db, "newsletter"), docIdFor(value));

      // 2) Duplicate check — if the doc exists, we short-circuit with a friendly note.
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setAlreadyIn(true);
          setEmail("");
          return;
        }
      } catch {
        /* read may be blocked by rules — fall through to setDoc merge, which is idempotent */
      }

      // 3) Write to Firestore. Rules only allow this exact schema.
      await setDoc(
        ref,
        {
          email: value,
          lang: language,
          createdAt: serverTimestamp(),
          source: "footer",
        },
        { merge: true },
      );

      // 4) Best-effort local cache so the same browser doesn't re-nag.
      try {
        const raw = localStorage.getItem(NEWSLETTER_KEY);
        const list: string[] = raw ? JSON.parse(raw) : [];
        if (!list.includes(value)) list.push(value);
        localStorage.setItem(NEWSLETTER_KEY, JSON.stringify(list));
      } catch { /* ignore quota / private mode */ }

      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 3000);
    } catch (err: unknown) {
      const code = (err as { code?: string } | null)?.code ?? "";
      if (code === "permission-denied") {
        setErrorMsg(t(
          "غير مسموح بالاشتراك حالياً.",
          "Subscriptions are not allowed right now.",
          "Inscriptions non autorisées pour le moment.",
        ));
      } else if (code === "unavailable" || !navigator.onLine) {
        setErrorMsg(t(
          "لا يوجد اتصال بالإنترنت.",
          "You appear to be offline.",
          "Vous semblez hors ligne.",
        ));
      } else {
        setErrorMsg(t(
          "تعذّر الاشتراك. حاول لاحقاً.",
          "Subscription failed. Please try again.",
          "Échec de l'inscription. Réessayez.",
        ));
      }
    } finally {
      setBusy(false);
    }
  }

  // Link groups — each entry is renderable in 3 languages.
  const browseLinks = [
    { to: "/movies",     label: t("أفلام", "Movies", "Films") },
    { to: "/series",     label: t("مسلسلات", "Series", "Séries") },
    { to: "/categories", label: t("التصنيفات", "Categories", "Catégories") },
    { to: "/sitemap",    label: t("الفهرس", "Sitemap", "Plan du site") },
  ] as const;

  const aboutLinks = [
    { to: "/about",   label: t("من نحن", "About Us", "À propos") },
    { to: "/contact", label: t("اتصل بنا", "Contact", "Contact") },
    { to: "/faq",     label: t("الأسئلة الشائعة", "FAQ", "FAQ") },
  ] as const;

  const legalLinks = [
    { to: "/privacy",    label: t("الخصوصية", "Privacy", "Confidentialité") },
    { to: "/terms",      label: t("شروط الاستخدام", "Terms", "Conditions") },
    { to: "/disclaimer", label: t("إخلاء المسؤولية", "Disclaimer", "Avertissement") },
    { to: "/cookies",    label: t("الكوكيز", "Cookies", "Cookies") },
  ] as const;

  return (
    <footer className="mt-20 border-t border-border bg-card/30">
      <div className="container mx-auto px-4 py-10 sm:py-12">
        {/* Brand always full width on mobile; promotes to 12-col grid on lg */}
        <div className="grid gap-8 grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-4 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <div className="grid place-items-center w-8 h-8 rounded-lg shrink-0" style={{ background: "var(--gradient-primary)" }}>
                <Play className="h-4 w-4 text-primary-foreground fill-current" />
              </div>
              <span className="font-extrabold text-lg truncate">
                Rawan<span className="text-gradient">UI</span>
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t(
                "قالب عصري وسريع لمواقع الأفلام والمسلسلات، مصمم بأحدث المعايير وأفضل تجربة مستخدم.",
                "A modern, fast template for movie & series sites, built with the latest standards and best UX.",
                "Un modèle moderne et rapide pour les sites de films et séries, conçu selon les meilleurs standards UX.",
              )}
            </p>
          </div>

          {/* 3 link columns: 3-up on mobile (no wrap) → 3-up on lg (8 cols) */}
          <div className="lg:col-span-5 grid grid-cols-3 gap-4 sm:gap-6 min-w-0">
            <LinkColumn title={t("تصفح", "Browse", "Naviguer")} items={browseLinks} />
            <LinkColumn title={t("الموقع", "Site", "Site")} items={aboutLinks} />
            <LinkColumn title={t("قانوني", "Legal", "Légal")} items={legalLinks} />
          </div>

          {/* Newsletter + socials */}
          <div className="lg:col-span-3 min-w-0">
            <h2 className="font-bold mb-3 text-[11px] uppercase tracking-wider text-muted-foreground">
              {t("النشرة البريدية", "Newsletter", "Newsletter")}
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              {t("اشترك ليصلك كل جديد", "Subscribe for the latest releases", "Recevez les nouveautés")}
            </p>
            <form onSubmit={subscribe} className="grid grid-cols-[minmax(0,1fr)_auto] gap-1.5" noValidate>
              <div className="relative min-w-0">
                <Mail className="absolute top-1/2 -translate-y-1/2 start-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="email"
                  required
                  maxLength={254}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errorMsg) setErrorMsg(null);
                    if (alreadyIn) setAlreadyIn(false);
                  }}
                  disabled={busy}
                  aria-invalid={!!errorMsg}
                  placeholder={t("بريدك الإلكتروني", "your@email.com", "votre@email.com")}
                  className="w-full ps-8 pe-2 py-2 bg-background border border-border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !email}
                className="btn-primary inline-flex items-center justify-center px-3 text-xs !py-2 shrink-0 disabled:opacity-50"
                aria-label={t("اشترك", "Subscribe", "S'inscrire")}
              >
                {busy ? "…" : subscribed ? <Check className="h-4 w-4" /> : t("اشترك", "Join", "OK")}
              </button>
            </form>
            <div role="status" aria-live="polite" aria-atomic="true">
              {errorMsg && <p className="mt-2 text-xs text-destructive">{errorMsg}</p>}
              {subscribed && !errorMsg && (
                <p className="mt-2 text-xs text-primary">
                  {t("تم الاشتراك بنجاح، شكراً لك!", "Subscribed successfully — thank you!", "Inscription réussie, merci !")}
                </p>
              )}
              {alreadyIn && !errorMsg && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("هذا البريد مشترك مسبقاً.", "This email is already subscribed.", "Cet e-mail est déjà inscrit.")}
                </p>
              )}
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              {([
                ["facebook", Facebook],
                ["x", Twitter],
                ["instagram", Instagram],
                ["telegram", Send],
                ["whatsapp", MessageCircle],
                ["github", Github],
              ] as const).map(([key, Icon]) => {
                const href = social[key];
                if (!href) return null;
                return (
                  <a key={key} href={href} target="_blank" rel="noopener noreferrer"
                     className="p-2 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground transition-colors shrink-0" aria-label={key}>
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="container mx-auto px-4 py-5 flex flex-wrap-reverse gap-2 items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            {t("صُمم بـ", "Crafted with", "Conçu avec")}
            <Heart className="h-3.5 w-3.5 fill-primary text-primary" />
            {t("لعشاق السينما", "for cinema lovers", "pour les cinéphiles")}
          </span>
          <span>© {new Date().getFullYear()} RawanUI — {t("جميع الحقوق محفوظة", "All rights reserved", "Tous droits réservés")}</span>
        </div>
      </div>
    </footer>
  );
}

function LinkColumn({
  title,
  items,
}: {
  title: string;
  items: readonly { to: string; label: string }[];
}) {
  return (
    <div className="min-w-0">
      <h2 className="font-bold mb-3 text-[11px] uppercase tracking-wider text-muted-foreground truncate">
        {title}
      </h2>
      <ul className="space-y-2 text-sm">
        {items.map((item) => (
          <li key={item.to} className="min-w-0">
            <Link
              to={item.to}
              className="block hover:text-primary transition truncate"
              title={item.label}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
